import { IUnitData, ManualObject } from "@/services/project/modal"
import { EUnitStatus } from "./types"
import {isNumber, isObject} from 'lodash-es'
import { UnitEntity } from "@/services/unitSet/unitSet.model"
import { Dimension } from "@/services/dimension/dimension.model"
import { LineRecord, NodeState, SetOperation } from "@/components/FlowSheetEditor/services/modal"
import { getUnitAttributes } from "@/components/FlowSheetEditor/components/Header/Simulation/Workbook/VariableTable/attributes"

export function getUnitStatus(data: IUnitData | undefined) {
  if (!data) return EUnitStatus.Unknown
  if (data.status === 0) return EUnitStatus.Ok
  if (data.status > 0 && data.status <= 99) return EUnitStatus.Warning
  if (data.status > 99) return EUnitStatus.Error
  return EUnitStatus.Unknown
}

export function getUnitStatusClassName(status: EUnitStatus) {
  switch (status) {
    case EUnitStatus.Error: {
      return 'unit-error'
    }
    case EUnitStatus.Warning: {
      return 'unit-warn'
    }
    case EUnitStatus.Ok: {
      return 'unit-ok'
    }
    default: {
      return null
    }
  }
}

export function numberFixTo(val: number, fixed = 3) {
  if (!isNumber(val)) return val;
  if (Number.isInteger(val)) return val;
  return parseFloat(val.toFixed(fixed))
}

export function numberFixToStr(val: any, fixed = 3): string {
  if (!isNumber(val)) return val;
  const strVal = val.toFixed(fixed);
  if (parseFloat(strVal) === 0) {
    // to avoid -0.00
    return Number(0).toFixed(fixed)
  }
  return strVal
}

export const toUnitVal = (val: number, unit: Dimension.Unit | undefined) => {
  if (!unit) {
    return val;
  }
  if (!isNumber(val)) {
    return val;
  }

  return val * unit.factor + unit.offset
}

export const toUnitValWithFormat = (val: number, unitEntity: UnitEntity) => {
  if (!unitEntity) return val;
  const unit = unitEntity.unit
  if (!unit) {
    return val;
  }
  if (!isNumber(val)) {
    return val;
  }

  return numberFixTo(toUnitVal(val, unit), unitEntity.cutoff)
}

export const toUnitValWithNoCutoff = (val: number, unitEntity: UnitEntity) => {
  if (!unitEntity) return val;
  const unit = unitEntity.unit
  if (!unit) {
    return val;
  }
  if (!isNumber(val)) {
    return val;
  }

  return toUnitVal(val, unit)
}

export const toUnitValStrWithFormat = (val: number, unitEntity?: UnitEntity) => {
  if (!unitEntity) {
    return val;
  }
  if (!isNumber(val)) {
    return val;
  }
  return numberFixToStr(toUnitVal(val, unitEntity.unit), unitEntity.cutoff ?? 4)
}

export const toDefaultUnitVal = (val: number, unit: Dimension.Unit | undefined) => {
  if (!unit) {
    return val;
  }
  if (!isNumber(val)) {
    return val;
  }

  return (val - unit.offset) / unit.factor
}

export const toUnitValFromUnit = (val: number, fromUnit: Dimension.Unit, toUnit: Dimension.Unit) => {
  const defUnitVal = toDefaultUnitVal(val, fromUnit)
  return toUnitVal(defUnitVal, toUnit)
}

export const getVarAttribute = (attr: string, unit: LineRecord | NodeState) => {
  const attributes = getUnitAttributes(unit)
  return attributes.find(item => item.value === attr)
}

export const variableLabel = (base: string, unitEntity?: UnitEntity) => {
  if (!unitEntity) return base;

  return `${base}[${unitEntity.unit.code}]`
}

export const getAttributeValueFromData = (data: any, attribute: string) => {
  const keys = attribute.split(':').filter(item => item !== '')
  return keys.reduce((res: any, currKey: string) => {
    return res?.[currKey]
  }, data || {})
}

export const getValueFromManual = (data: number | ManualObject) => {
  if (data  && !Array.isArray(data) && isObject(data)) {
    return (data as ManualObject).val
  }

  return data
}